interface CanvasDrawFailure {
  image_type: string;
  image_width: number | null;
  image_height: number | null;
  source_width: number | null;
  source_height: number | null;
  destination_width: number | null;
  destination_height: number | null;
  canvas_width: number;
  canvas_height: number;
  stack: string;
}

const failures = new WeakMap<Error, CanvasDrawFailure>();

export function getCanvasDrawFailure(error: Error): CanvasDrawFailure | undefined {
  return failures.get(error);
}

export function installCanvasErrorDiagnostics(): () => void {
  if (!/Version\/15\./.test(window.navigator.userAgent) || !('CanvasRenderingContext2D' in globalThis)) return () => {};
  const prototype = CanvasRenderingContext2D.prototype;
  const original = prototype.drawImage;
  function drawImage(this: CanvasRenderingContext2D, image: CanvasImageSource, dx: number, dy: number, ...dimensions: number[]): void {
    try {
      const draw = original.bind(this);
      if (dimensions.length === 0) draw(image, dx, dy);
      else if (dimensions.length === 2) draw(image, dx, dy, dimensions[0], dimensions[1]);
      else draw(image, dx, dy, dimensions[0], dimensions[1], dimensions[2], dimensions[3], dimensions[4], dimensions[5]);
    } catch (error) {
      if (error instanceof Error && error.name === 'IndexSizeError') {
        try {
          let imageType = 'other';
          let width: number | null = null;
          let height: number | null = null;
          if (image instanceof HTMLImageElement) {
            imageType = 'image';
            width = image.naturalWidth;
            height = image.naturalHeight;
          } else if (image instanceof HTMLVideoElement) {
            imageType = 'video';
            width = image.videoWidth;
            height = image.videoHeight;
          } else if (image instanceof HTMLCanvasElement) {
            imageType = 'canvas';
            width = image.width;
            height = image.height;
          } else if ('ImageBitmap' in globalThis && image instanceof ImageBitmap) {
            imageType = 'bitmap';
            width = image.width;
            height = image.height;
          }
          const stack = (error.stack ?? '').split('\n').flatMap((frame) => {
            const match = frame.match(/https?:\/\/[^\s/]+\/(?:_astro|src|node_modules)\/[^\s?#)]+/);
            return match ? [match[0]] : [];
          }).join(' | ').slice(0, 2200);
          failures.set(error, {
            image_type: imageType,
            image_width: width,
            image_height: height,
            source_width: dimensions.length === 6 ? dimensions[0] : width,
            source_height: dimensions.length === 6 ? dimensions[1] : height,
            destination_width: dimensions.length === 6 ? dimensions[4] : dimensions[0] ?? width,
            destination_height: dimensions.length === 6 ? dimensions[5] : dimensions[1] ?? height,
            canvas_width: this.canvas.width,
            canvas_height: this.canvas.height,
            stack,
          });
        } catch {
          // Diagnostic failure must not replace the original canvas exception.
        }
      }
      throw error;
    }
  }
  prototype.drawImage = drawImage;
  return () => {
    if (prototype.drawImage === drawImage) prototype.drawImage = original;
  };
}
